/**
 * Modal utility functions for handling focus and accessibility
 */

/**
 * Safely blur the currently focused element to prevent aria-hidden accessibility warnings
 * when closing modals or hiding elements.
 * 
 * This prevents the browser warning:
 * "Blocked aria-hidden on an element because its descendant retained focus"
 * 
 * @example
 * ```tsx
 * const handleClose = () => {
 *   blurActiveElement();
 *   setModalOpen(false);
 * };
 * ```
 */
export const blurActiveElement = (): void => {
  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur();
  }
};

/**
 * Safely blur the currently focused element with a delay to ensure the blur completes
 * before the modal closes. Returns a promise that resolves after the blur.
 * 
 * @param delayMs - Milliseconds to wait after blurring (default: 10ms)
 * @returns Promise that resolves after blur completes
 * 
 * @example
 * ```tsx
 * const handleClose = async () => {
 *   await blurActiveElementAsync();
 *   setModalOpen(false);
 * };
 * ```
 */
export const blurActiveElementAsync = (delayMs: number = 10): Promise<void> => {
  return new Promise((resolve) => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    // Small delay to ensure blur completes before modal closes
    setTimeout(resolve, delayMs);
  });
};

/**
 * Creates a safe close handler for modals that automatically blurs the active element
 * before calling the provided close function.
 * 
 * @param onClose - The function to call after blurring
 * @returns A wrapped close handler that blurs before closing
 * 
 * @example
 * ```tsx
 * const handleClose = createSafeCloseHandler(() => {
 *   setModalOpen(false);
 * });
 * ```
 */
export const createSafeCloseHandler = (onClose: () => void): (() => void) => {
  return () => {
    blurActiveElement();
    // Use requestAnimationFrame to ensure blur happens before close
    requestAnimationFrame(() => {
      onClose();
    });
  };
};
